import { useEffect, useState } from 'react';
import { BarChart3, Search, Save, Users, CreditCard, Gift, Activity } from 'lucide-react';
import api from '../utils/api';

const plans = ['basico', 'pro', 'premium'];

export default function OwnerAdminPage() {
  const [adminToken, setAdminToken] = useState(localStorage.getItem('saasAdminToken') || '');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [discounts, setDiscounts] = useState({});
  const [saasMpToken, setSaasMpToken] = useState('');

  const headers = adminToken ? { 'x-saas-admin-token': adminToken } : {};

  const loadData = async () => {
    if (!adminToken) return;
    setLoading(true);
    try {
      localStorage.setItem('saasAdminToken', adminToken);
      const response = await api.get('/admin/owner-dashboard', {
        params: { status: status || undefined, q: q || undefined },
        headers
      });
      setData(response.data);
      setSaasMpToken('');
      const next = {};
      (response.data.discounts || []).forEach((item) => {
        next[item.plan] = item;
      });
      setDiscounts(next);
    } catch (error) {
      alert(error.response?.data?.message || error.response?.data?.error || 'Erro ao carregar painel admin');
    } finally {
      setLoading(false);
    }
  };

  const saveDiscount = async (plan) => {
    const current = discounts[plan] || {};
    await api.put(`/admin/discounts/${plan}`, {
      discount_percent: current.discount_percent || 0,
      active: !!current.active,
      note: current.note || ''
    }, { headers });
    await loadData();
  };

  const saveSettings = async () => {
    await api.put('/admin/settings', {
      mercado_pago_token: saasMpToken
    }, { headers });
    setSaasMpToken('');
    await loadData();
  };

  useEffect(() => {
    if (adminToken) loadData();
  }, []);

  const overview = data?.overview || {};

  return (
    <div className="min-h-screen bg-dark-900 text-white p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="card p-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-primary-400" />
                Painel do Dono
              </h1>
              <p className="text-gray-400 mt-1">Controle de planos, trials, uso e vendas.</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="password"
                value={adminToken}
                onChange={(e) => setAdminToken(e.target.value)}
                className="input min-w-64"
                placeholder="Token admin"
              />
              <button onClick={loadData} disabled={loading || !adminToken} className="btn-primary px-5">
                {loading ? 'Carregando...' : 'Entrar'}
              </button>
            </div>
          </div>
        </div>

        {data && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Barbearias', value: overview.total_barbershops || 0, icon: Users },
                { label: 'Pagando', value: overview.active_paid || 0, icon: CreditCard },
                { label: 'MRR estimado', value: `R$ ${(overview.mrr || 0).toFixed(2)}`, icon: Activity },
                { label: 'Receita do mês', value: `R$ ${(overview.current_month_revenue || 0).toFixed(2)}`, icon: BarChart3 },
                { label: '7 dias gratis', value: overview.trial_barbershops || 0, icon: Gift },
                { label: 'Trial vencendo', value: overview.trial_expiring_soon || 0, icon: Gift },
                { label: 'Risco churn', value: overview.churn_risk || 0, icon: Activity },
                { label: 'Potencial mensal', value: `R$ ${(overview.monthly_potential || 0).toFixed(2)}`, icon: Activity }
              ].map((item) => (
                <div key={item.label} className="card p-5 border border-dark-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-400">{item.label}</p>
                      <p className="text-2xl font-bold mt-1">{item.value}</p>
                    </div>
                    <item.icon className="w-8 h-8 text-primary-400" />
                  </div>
                </div>
              ))}
            </div>

            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">Mercado Pago do SaaS</h2>
              <div className="grid lg:grid-cols-[1fr_auto] gap-3 items-end">
                <div>
                  <label className="label">Access token para cobrar planos e renovacoes</label>
                  <input
                    type="password"
                    value={saasMpToken}
                    onChange={(e) => setSaasMpToken(e.target.value)}
                    className="input"
                    placeholder={data.settings?.mercado_pago_configured ? `Configurado via ${data.settings?.mercado_pago_source}` : 'Cole o access token do Mercado Pago'}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Esse token e usado somente pelo backend para gerar Pix de assinatura.
                  </p>
                </div>
                <button onClick={saveSettings} disabled={!saasMpToken} className="btn-primary">
                  <Save className="w-4 h-4 mr-2" />
                  Salvar token
                </button>
              </div>
            </div>

            <div className="card p-6">
              <h2 className="text-lg font-semibold mb-4">Desconto por plano</h2>
              <div className="grid md:grid-cols-3 gap-4">
                {plans.map((plan) => {
                  const current = discounts[plan] || { discount_percent: 0, active: false, note: '' };
                  return (
                    <div key={plan} className="border border-dark-700 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold capitalize">{plan}</p>
                        <label className="flex items-center gap-2 text-sm text-gray-300">
                          <input
                            type="checkbox"
                            checked={!!current.active}
                            onChange={(e) => setDiscounts(prev => ({ ...prev, [plan]: { ...current, active: e.target.checked } }))}
                          />
                          Ativo
                        </label>
                      </div>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={current.discount_percent || 0}
                        onChange={(e) => setDiscounts(prev => ({ ...prev, [plan]: { ...current, discount_percent: Number(e.target.value) } }))}
                        className="input"
                        placeholder="% desconto"
                      />
                      <input
                        type="text"
                        value={current.note || ''}
                        onChange={(e) => setDiscounts(prev => ({ ...prev, [plan]: { ...current, note: e.target.value } }))}
                        className="input"
                        placeholder="Observação"
                      />
                      <button onClick={() => saveDiscount(plan)} className="btn-primary w-full">
                        <Save className="w-4 h-4 mr-2" />
                        Salvar
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="card p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
                <h2 className="text-lg font-semibold">Barbearias</h2>
                <div className="flex flex-col sm:flex-row gap-2">
                  <select value={status} onChange={(e) => setStatus(e.target.value)} className="input">
                    <option value="">Todos os status</option>
                    <option value="trial">7 dias gratis</option>
                    <option value="ativo">Pagando</option>
                    <option value="atrasado">Atrasado</option>
                    <option value="cancelado">Cancelado</option>
                  </select>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} className="input pl-10" placeholder="Buscar nome" />
                  </div>
                  <button onClick={loadData} className="btn-primary px-4">Filtrar</button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-gray-400 border-b border-dark-700">
                    <tr>
                      <th className="py-3 pr-4">Nome</th>
                      <th className="py-3 pr-4">ID</th>
                      <th className="py-3 pr-4">Indicação</th>
                      <th className="py-3 pr-4">Plano</th>
                      <th className="py-3 pr-4">Status</th>
                      <th className="py-3 pr-4">Uso 7d</th>
                      <th className="py-3 pr-4">Expira</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.barbershops.map((shop) => (
                      <tr key={shop.id} className="border-b border-dark-800">
                        <td className="py-3 pr-4 font-medium">{shop.nome}</td>
                        <td className="py-3 pr-4 text-gray-400 font-mono text-xs">{shop.id}</td>
                        <td className="py-3 pr-4 text-gray-400 font-mono text-xs">{shop.referral_code || '-'}</td>
                        <td className="py-3 pr-4 capitalize">{shop.plano}</td>
                        <td className="py-3 pr-4">{shop.status_pagamento}</td>
                        <td className="py-3 pr-4">{shop.appointments_7d} agend.</td>
                        <td className="py-3 pr-4">{shop.days_remaining ?? '-'} dias</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
